// src/client/renderer/vfx/tile/landing-fx-pool.ts
//
// Plan 16, Task 3.1 — Landing FX pool (tile-type variant lookup).
//
// A one-shot particle puff + ring triggered by LandingEvent.
// One pool with table-driven colour/intensity variants; max-active=4.
//
// Geometry: SphereGeometry (puff disc effect centred on tile).
// One shared geometry; per-emitter MeshBasicMaterial clone.
//
// Animation timeline (gsap, manual-ticker via GsapRegistry):
//   0.000–0.350s  scale 0.0 → 1.5  + opacity 1.0 → 0.0
// Total lifetime: 350ms. One-shot — isFinished returns true once the tl.call
// callback fires at 0.35s. Use tl.call(fn, undefined, abs) — NOT eventCallback.
//
// Forward-defense F4: pool.dispose() MUST iterate active set + free per-emitter
// materials + shared geometry + kill all active timelines without expecting
// prior pool.release() calls.

import * as THREE from 'three';
import type { TileVfxPool, TileVfxSpawnArgs } from './tile-vfx-types';
import type { GsapRegistry } from '../gsap-registry';
import type { GameEvent } from '../../../../shared/game/types';
import type { TileType } from '../../../../shared/board/types';

// ─────────────────────────────────────────────────────────────────────────────
// Variant data literal (verbatim from plan)
// ─────────────────────────────────────────────────────────────────────────────

export const LANDING_FX_VARIANTS: Record<TileType, { color: number; intensity: number }> = {
  start: { color: 0xffffff, intensity: 0.4 },
  gold: { color: 0xffd24a, intensity: 0.8 },
  heal: { color: 0x6dd56b, intensity: 0.7 },
  hazard: { color: 0xd93636, intensity: 0.9 },
  item: { color: 0x6aa9ff, intensity: 0.7 },
  soul_anchor: { color: 0xc063ff, intensity: 0.85 },
  key_shop: { color: 0x2fc0a0, intensity: 0.65 },
  weapons_cache: { color: 0xb25a35, intensity: 0.8 },
  mystery_box: { color: 0x5a50c8, intensity: 0.75 },
  event: { color: 0xffaa33, intensity: 0.75 },
  graveyard: { color: 0x6b6b8a, intensity: 0.5 },
  pathing: { color: 0xffffff, intensity: 0.3 },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Animation constants
// ─────────────────────────────────────────────────────────────────────────────

const SCALE_START = 0.0;
const SCALE_END = 1.5;
const LIFETIME = 0.35; // seconds (350ms)

/** Max concurrent active emitters. 5th acquire while 4 active returns null. */
const MAX_ACTIVE = 4;

// ─────────────────────────────────────────────────────────────────────────────
// Geometry constants (SphereGeometry — puff disc)
// ─────────────────────────────────────────────────────────────────────────────

const SPHERE_RADIUS = 0.4;
const SPHERE_W_SEGS = 8;
const SPHERE_H_SEGS = 6;

// ─────────────────────────────────────────────────────────────────────────────
// Internal emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<GsapRegistry['create']>;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** The known TileType strings, derived from LANDING_FX_VARIANTS keys at runtime. */
const KNOWN_TILE_TYPES = new Set<string>(Object.keys(LANDING_FX_VARIANTS));

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export interface LandingFxPoolConfig {
  readonly scene: THREE.Scene;
  readonly registry: GsapRegistry;
}

export function createLandingFxPool({ scene, registry }: LandingFxPoolConfig): TileVfxPool {
  // Shared geometry — disposed exactly once in pool.dispose()
  const sharedGeometry = new THREE.SphereGeometry(SPHERE_RADIUS, SPHERE_W_SEGS, SPHERE_H_SEGS);

  // Per-emitter state map: emitter → { handle, finished }
  const emitterStates = new Map<THREE.Object3D, EmitterState>();

  let disposed = false;

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(args: TileVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // Enforce max-active cap
    if (emitterStates.size >= MAX_ACTIVE) return null;

    // Determine variant (use args.tileType if set; fall back to generic white burst)
    const tileType = args.tileType as TileType | undefined;
    const variant =
      tileType && LANDING_FX_VARIANTS[tileType]
        ? LANDING_FX_VARIANTS[tileType]
        : { color: 0xffffff, intensity: 0.5 };

    // Per-emitter material clone (additive blend — bright on any background)
    const material = new THREE.MeshBasicMaterial({
      color: variant.color,
      transparent: true,
      opacity: variant.intensity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(sharedGeometry, material);
    mesh.position.set(args.position.x, args.position.y, args.position.z);
    mesh.scale.set(SCALE_START, SCALE_START, SCALE_START);

    scene.add(mesh);

    // Build gsap timeline via registry
    const handle = registry.create('landing_fx');
    const tl = handle.timeline;

    const state: EmitterState = { handle, finished: false };

    // Tween: scale 0.0 → 1.5 over 350ms + opacity proxy from intensity → 0
    tl.to(
      mesh.scale,
      {
        x: SCALE_END,
        y: SCALE_END,
        z: SCALE_END,
        duration: LIFETIME,
        ease: 'power2.out',
      },
      0,
    );

    const opacityProxy = { value: variant.intensity };
    tl.to(
      opacityProxy,
      {
        value: 0.0,
        duration: LIFETIME,
        ease: 'power2.in',
        onUpdate: () => {
          material.opacity = opacityProxy.value;
        },
      },
      0,
    );

    // Completion callback — use tl.call(fn, undefined, absolutePos) per spec.
    // Do NOT use eventCallback('onComplete') — see gsap-registry.ts VIRTUAL_DURATION note.
    tl.call(
      () => {
        state.finished = true;
      },
      undefined,
      LIFETIME,
    );

    emitterStates.set(mesh, state);
    return mesh;
  }

  // ── update ─────────────────────────────────────────────────────────────────

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    return new Set(emitterStates.keys());
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const state = emitterStates.get(emitter);
    if (!state) return true;
    return state.finished;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    const state = emitterStates.get(emitter);
    if (!state) return;

    state.handle.dispose();
    emitterStates.delete(emitter);

    scene.remove(emitter);

    const mesh = emitter as THREE.Mesh;
    if (mesh.material instanceof THREE.Material) {
      mesh.material.dispose();
    }
  }

  // ── dispose ────────────────────────────────────────────────────────────────
  // Forward-defense F4: iterates active emitters and frees all GPU resources
  // WITHOUT expecting prior release() calls.

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    for (const [emitter, state] of emitterStates) {
      state.handle.dispose();
      scene.remove(emitter);
      const mesh = emitter as THREE.Mesh;
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
    }
    emitterStates.clear();

    // Dispose shared geometry exactly once
    sharedGeometry.dispose();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a `LandingEvent` to landing-FX spawn args.
 *
 * Returns null for:
 *   - any non-landing event
 *   - landing events where event.tileType is not one of the 11 known TileType values
 *
 * Unknown tileType strings produce null — caller decides how to surface warnings.
 */
export function resolveLandingFxForEvent(
  event: GameEvent,
): { poolId: 'landing_fx'; args: TileVfxSpawnArgs } | null {
  if (event.type !== 'landing') return null;

  const tileType = event.tileType;
  if (!KNOWN_TILE_TYPES.has(tileType)) return null;

  return {
    poolId: 'landing_fx',
    args: {
      position: { x: 0, y: 0, z: 0 }, // position resolved at wire-up (Task 5.1)
      tileType: tileType as TileType,
    },
  };
}
